import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Info } from "lucide-react";
import { PROJECTS } from "../lib/content";
import { scrollToId } from "../lib/site";
import { Reveal, Eyebrow, MaskLines, EASE } from "./Reveal";

const FILTERS = [{ key: "todos", label: "Todos" }, ...PROJECTS.map((p) => ({ key: p.key, label: p.category }))];

const Mockup = ({ p }) => (
  <div className="relative px-4 pb-8 pt-6 sm:px-8">
    <div className="relative rounded-t-xl border border-white/15 bg-[#1b2130] p-1.5 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)]">
      <div className="flex items-center gap-1.5 px-2 pb-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-white/30" /><span className="h-1.5 w-1.5 rounded-full bg-white/30" /><span className="h-1.5 w-1.5 rounded-full bg-white/30" />
        <span className="ml-2 h-3 flex-1 rounded bg-white/5" />
      </div>
      <div className="relative aspect-[16/10] overflow-hidden rounded-md">
        <img src={p.img} alt={`Demonstração conceitual de site para ${p.category.toLowerCase()}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
        <div className="absolute left-4 top-1/2 -translate-y-1/2">
          <p className="font-mono-jl text-[9px] uppercase tracking-[0.25em] text-[#7FE3FF]">{p.category}</p>
          <p className="font-display mt-1 max-w-[60%] text-base font-semibold leading-tight text-white sm:text-lg">{p.name}</p>
          <span className="mt-2 inline-block rounded-full bg-[#087BFF] px-2.5 py-1 text-[9px] font-semibold text-white">Fale conosco</span>
        </div>
      </div>
    </div>
    <div className="mx-auto h-3 w-[108%] -translate-x-[3.7%] rounded-b-xl bg-gradient-to-b from-[#3a4252] to-[#1b2130]" />
    <div className="absolute bottom-3 right-2 w-[24%] rounded-[14px] border border-white/20 bg-[#0d111a] p-1 shadow-[0_20px_40px_rgba(0,0,0,0.7)] transition-transform duration-700 group-hover:-translate-y-3 sm:right-5">
      <div className="aspect-[9/19] overflow-hidden rounded-[10px]">
        <img src={p.img} alt="" loading="lazy" className="h-full w-full object-cover" />
      </div>
    </div>
  </div>
);

export const Portfolio = () => {
  const [filter, setFilter] = useState("todos");
  const list = PROJECTS.filter((p) => filter === "todos" || p.key === filter);

  const want = (p) => {
    window.dispatchEvent(new CustomEvent("jl:prefill", { detail: p.category }));
    scrollToId("contato");
  };

  return (
    <section id="portfolio" data-testid="portfolio-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal><Eyebrow>03 — Portfólio</Eyebrow></Reveal>
        <div className="mt-6 grid gap-8 lg:grid-cols-12 lg:items-end">
          <h2 className="font-display text-3xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:col-span-8">
            <MaskLines inView lines={[<>Projetos que transformam negócios</>, <>em <span className="text-grad">marcas digitais.</span></>]} />
          </h2>
          <Reveal delay={0.15} className="lg:col-span-4">
            <p data-testid="portfolio-disclaimer" className="flex gap-3 rounded-2xl border border-[#00C8FF]/20 bg-[#00C8FF]/5 p-4 text-sm text-slate-300">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#00C8FF]" />
              Os exemplos abaixo são demonstrações conceituais de sites que a JL Nexus pode desenvolver, não trabalhos de clientes.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 flex gap-2 overflow-x-auto pb-2 [scrollbar-width:none]" role="tablist" aria-label="Filtrar por categoria">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={filter === f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`portfolio-filter-${f.key}`}
              className={`relative shrink-0 rounded-full border px-5 py-2.5 text-sm transition-colors duration-300 ${filter === f.key ? "border-[#087BFF] text-white" : "border-white/15 text-slate-300 hover:border-white/40 hover:text-white"}`}
            >
              {filter === f.key && <motion.span layoutId="pf-pill" className="absolute inset-0 -z-10 rounded-full bg-[#087BFF]/25" />}
              {f.label}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {list.map((p) => (
              <motion.article
                layout
                key={p.key}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.5, ease: EASE }}
                data-testid={`portfolio-card-${p.key}`}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-[#141925] to-[#0B0F18] transition-[border-color,box-shadow] duration-500 hover:border-[#087BFF]/50 hover:shadow-[0_0_60px_-15px_rgba(8,123,255,0.5)]"
              >
                <div className="pointer-events-none absolute left-1/2 top-0 h-40 w-3/4 -translate-x-1/2 rounded-full bg-[#087BFF]/15 blur-3xl" />
                <Mockup p={p} />
                <div className="border-t border-white/10 p-6">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono-jl text-[11px] uppercase tracking-[0.2em] text-[#7FE3FF]">{p.category}</span>
                    <span className="rounded-full border border-white/15 px-2.5 py-1 text-[11px] text-slate-300">Demonstração conceitual</span>
                  </div>
                  <h3 className="font-display mt-3 text-xl font-semibold text-white">{p.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">{p.desc}</p>
                  <button type="button" onClick={() => want(p)} data-testid={`portfolio-want-${p.key}`} className="group/btn mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#00C8FF] transition-colors hover:text-white">
                    Quero um site assim
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                  </button>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
